import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import OptionIcon from 'src/shared/material_icons/image/image.svg';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import styled from 'styled-components';
import {
    COMPOSER_LABEL_COLUMN,
    ComposerPanelAddButton,
    ComposerFieldNote,
    ComposerIconButton,
    ComposerRowActionButton,
    ComposerSettingsBody,
    ComposerToolbar,
    ComposerToolbarSpacer,
    composerFieldDensity
} from '../composerWorkspace';
import { ComposerBlock, ComposerBlockList, ComposerNewButton } from '../composerBlock';
import { DropdownActionItem, DropdownItemIcon } from '../../../../../../widgets/dropdown';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import { CollapseAllIcon, ExpandAllIcon, SingleBranchIcon } from 'src/components/widgets/icons/scene';
import { ComposerChoice, ComposerSlot, ComposerSlotList, ComposerSlotOption } from '../composerSlots';
import {
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerLogicalOperator,
    getComposerLinkOptionIDs,
    isComposerMultiOptionLink,
    joinComposerLinkOptionIDs
} from './composerAttributesModels';
import { ComposerValidationErrorCode } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import { ConfirmDropdownItem } from '../../confirmTwice';
import { requestComposerReveal } from '../composerReveal';

const Rules = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    ${composerFieldDensity}
`;

// IL TITOLO DEL PANNELLO, IN MAIUSCOLETTO COME LE SECTION: chi decide se il soggetto si vede.
const Lead = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const LeadTitle = styled.span`
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const ApplyNote = styled.p`
    margin: 0;
    color: var(--editor-muted, #55595f);
    font-size: 11px;
    line-height: 16px;
`;

const HeadSlot = styled.div`
    width: 100%;
    min-width: 0;
`;

// NEL CORPO DELLA CONDIZIONE IL NOME A SINISTRA, IL CONTROLLO A DESTRA (prototipo, Match e Choice).
const ConditionRow = styled.div`
    display: grid;
    min-width: 0;
    grid-template-columns: ${COMPOSER_LABEL_COLUMN} minmax(0, 1fr);
    align-items: center;
    gap: 10px;

    /* Etichette delle card come nel prototipo: 11px, peso normale, grigio tenue. */
    > span:first-child {
        color: var(--editor-faint, #676a6e);
        font-size: 11px;
        font-weight: 400;
    }
`;

interface ComposerLinksEditorProps {
    attributes: ComposerAttributeNode[];
    links: ComposerAttributeNodeLink[];
    logicalOperator?: ComposerLogicalOperator;
    selfAttributeID?: string;
    subjectLabel: string;
    validationErrors?: ReadonlyMap<string, string>;
    onChange: (links: ComposerAttributeNodeLink[], logicalOperator: ComposerLogicalOperator) => void;
}

const ComposerLinksEditor: FC<ComposerLinksEditorProps> = ({
    attributes,
    links,
    logicalOperator = ComposerLogicalOperator.AND,
    selfAttributeID,
    subjectLabel,
    validationErrors,
    onChange
}) => {
    const availableAttributes = attributes.filter(
        (attribute) => attribute.type === ComposerAttributeType.Attribute && attribute.id !== selfAttributeID && attribute.options.length > 0
    );
    const attributeOptions: ComposerSlotOption[] = availableAttributes.map((attribute) => ({
        value: attribute.id,
        label: attribute.name,
        caption: attribute.options.length === 1 ? T._('1 option', 'Admin') : stringFormat(T._('{0} options', 'Admin'), attribute.options.length.toString())
    }));
    // AND / OR (prototipo V3): una scelta a due, e sotto che cosa vuol dire. Con meno di due
    // espressioni non c'è niente da combinare, e la scelta resta spenta.
    const logicalOptions = [
        { value: ComposerLogicalOperator.AND, label: T._('AND', 'Admin') },
        { value: ComposerLogicalOperator.OR, label: T._('OR', 'Admin') }
    ];
    const logicalSays =
        logicalOperator === ComposerLogicalOperator.OR
            ? T._('Shown when at least one condition is true.', 'Admin')
            : T._('Shown when every condition is true.', 'Admin');
    // «is / is not», come lo dice il prototipo: si legge di seguito, «Colore is Rosso».
    //
    // «IS ONE OF / IS NOT ONE OF» NON SI OFFRONO (utente, 2026-10-15). Il motore 3D li valuta (In / NotIn,
    // `ComposerSelection`), ma il Backoffice ha sempre scritto solo = e ≠, e che il backend salvi e usi gli
    // altri due non è verificato. Un'espressione che li porta già — scritta altrove — si legge e si modifica
    // ancora, con le sue opzioni una per riga; il confronto si può riportare a «is» o «is not».
    const comparisonOptions = [
        { value: ComposerComparisonOperator.EQUAL, label: T._('is', 'Admin') },
        { value: ComposerComparisonOperator.NOT_EQUAL, label: T._('is not', 'Admin') }
    ];
    const hasLinkError = (index: number, ...codes: string[]) => codes.some((code) => validationErrors?.has(`${code}:${index}`));

    const updateLink = (index: number, nextLink: ComposerAttributeNodeLink) => {
        const nextLinks = links.map((link, linkIndex) => (linkIndex === index ? nextLink : link));
        onChange(nextLinks, logicalOperator);
    };

    // PASSANDO DA UNA A PIÙ OPZIONI LA SCELTA NON SI PERDE: «is Rosso» diventa «is one of Rosso», e tornando
    // indietro resta la prima delle scelte.
    const changeOperation = (index: number, link: ComposerAttributeNodeLink, operation: ComposerComparisonOperator) => {
        const optionIDs = getComposerLinkOptionIDs(link);
        const optionID = isComposerMultiOptionLink(operation) ? joinComposerLinkOptionIDs(optionIDs) : (optionIDs[0] ?? '');
        updateLink(index, { ...link, operation, optionID });
    };

    // A new expression starts empty, so the placeholders ask for a conscious choice instead of
    // silently linking the first attribute; validation flags it until both selects are filled.
    const addLink = () => {
        onChange([...links, { attributeID: '', optionID: '', operation: ComposerComparisonOperator.EQUAL }], logicalOperator);
        openCard(links.length);
    };

    // LE ESPRESSIONI SONO CARD CHE SI CHIUDONO, come le azioni (utente, 2026-10-15): la barra in alto ha
    // «Add», una card alla volta (acceso di partenza) e apri/chiudi tutte. Chiusa, la card dice in una riga
    // che cosa chiede — «is Red» sotto il nome dell'attributo.
    const [openIndexes, setOpenIndexes] = React.useState<number[]>([]);
    const [singleCard, setSingleCard] = React.useState(true);
    const openCard = (index: number) => setOpenIndexes((current) => (singleCard ? [index] : current.includes(index) ? current : [...current, index]));
    const toggleCard = (index: number) =>
        openIndexes.includes(index) ? setOpenIndexes((current) => current.filter((candidate) => candidate !== index)) : openCard(index);
    const removeLink = (index: number) => {
        onChange(
            links.filter((_, linkIndex) => linkIndex !== index),
            logicalOperator
        );
        setOpenIndexes((current) => current.filter((candidate) => candidate !== index).map((candidate) => (candidate > index ? candidate - 1 : candidate)));
    };
    const summaryOf = (link: ComposerAttributeNodeLink, attribute?: ComposerAttributeNode) => {
        if (!attribute) return undefined;
        const names = getComposerLinkOptionIDs(link)
            .map((id) => attribute.options.find((option) => option.id === id)?.name)
            .filter(Boolean)
            .join(', ');
        const verb = comparisonOptions.find((option) => option.value === link.operation)?.label ?? link.operation;
        return names ? `${verb} ${names}` : stringFormat(T._('{0} — no option yet', 'Admin'), verb);
    };

    const wordOf = (index: number) =>
        index === 0 ? T._('If', 'Admin') : logicalOperator === ComposerLogicalOperator.OR ? T._('Or if', 'Admin') : T._('And if', 'Admin');
    const cloneLink = (index: number) => {
        const nextLinks = [...links.slice(0, index + 1), { ...links[index] }, ...links.slice(index + 1)];
        onChange(nextLinks, logicalOperator);
        openCard(index + 1);
    };

    return (
        <>
            <ComposerToolbar>
                <ComposerPanelAddButton disabled={availableAttributes.length === 0} onClick={addLink}>
                    <PlusIcon />
                    {T._('Add', 'Admin')}
                </ComposerPanelAddButton>
                <ComposerToolbarSpacer />
                <ZTooltip title={singleCard ? T._('One card at a time: opening a card closes the others', 'Admin') : T._('One card at a time', 'Admin')}>
                    <ComposerIconButton
                        aria-label={T._('One card at a time', 'Admin')}
                        aria-pressed={singleCard}
                        $active={singleCard}
                        onClick={() => {
                            if (!singleCard) setOpenIndexes([]);
                            setSingleCard(!singleCard);
                        }}
                    >
                        <SingleBranchIcon />
                    </ComposerIconButton>
                </ZTooltip>
                <ZTooltip
                    title={
                        singleCard && openIndexes.length === 0
                            ? T._('One card at a time is on: open a single card from its head', 'Admin')
                            : openIndexes.length > 0
                              ? T._('Collapse all', 'Admin')
                              : T._('Expand all', 'Admin')
                    }
                >
                    <ComposerIconButton
                        aria-label={openIndexes.length > 0 ? T._('Collapse all', 'Admin') : T._('Expand all', 'Admin')}
                        disabled={links.length === 0 || (singleCard && openIndexes.length === 0)}
                        onClick={() => setOpenIndexes(openIndexes.length > 0 ? [] : links.map((_, index) => index))}
                    >
                        {openIndexes.length > 0 ? <CollapseAllIcon /> : <ExpandAllIcon />}
                    </ComposerIconButton>
                </ZTooltip>
            </ComposerToolbar>
            <ComposerSettingsBody>
                <Rules>
                    {/* «… IS SHOWN IF» E AND / OR (prototipo, utente 2026-10-20): il titolo, la scelta — spenta finché c'è una
                        condizione sola — e che cosa vuol dire. Ogni condizione porta davanti la parola che la lega alle altre. */}
                    <Lead>
                        <LeadTitle>{stringFormat(T._('{0} is shown if', 'Admin'), subjectLabel)}</LeadTitle>
                        <ZTooltip title={links.length < 2 ? T._('Add a second condition to combine them', 'Admin') : undefined}>
                            <div>
                                <ComposerChoice
                                    ariaLabel={T._('Combine the conditions', 'Admin')}
                                    options={logicalOptions}
                                    value={logicalOperator}
                                    disabled={links.length < 2}
                                    onChange={(operator) => onChange(links, operator)}
                                />
                            </div>
                        </ZTooltip>
                        <ApplyNote>{logicalSays}</ApplyNote>
                    </Lead>
                    {links.length === 0 && <ComposerFieldNote>{stringFormat(T._('{0} is always shown.', 'Admin'), subjectLabel)}</ComposerFieldNote>}
                    <ComposerBlockList>
                        {links.map((link, index) => {
                            const linkedAttribute = availableAttributes.find((attribute) => attribute.id === link.attributeID);
                            const optionOptions: ComposerSlotOption[] = (linkedAttribute?.options ?? []).map((option) => ({
                                value: option.id,
                                label: option.name,
                                preview: option.img ? <img src={option.img} alt='' /> : undefined
                            }));
                            const multiple = isComposerMultiOptionLink(link.operation);
                            const chosenIDs = getComposerLinkOptionIDs(link);
                            const optionError = hasLinkError(
                                index,
                                ComposerValidationErrorCode.LINK_INCOMPLETE,
                                ComposerValidationErrorCode.LINK_OPTION_NOT_FOUND
                            );
                            const summary = summaryOf(link, linkedAttribute);
                            const headOptions = attributeOptions.map((option) =>
                                option.value === link.attributeID && summary ? { ...option, caption: summary } : option
                            );
                            return (
                                // La chiave non porta le opzioni: scegliendone una in più il menu deve restare aperto.
                                <ComposerBlock
                                    key={`${index}_${link.attributeID}`}
                                    id={`link-${index}`}
                                    open={openIndexes.includes(index)}
                                    onToggleOpen={() => toggleCard(index)}
                                    word={wordOf(index)}
                                    title={
                                        // L'ATTRIBUTO CHE DECIDE È LO SLOT DEL TITOLO: il clic lo ritrova, il menu lo cambia.
                                        <HeadSlot onClick={(event) => event.stopPropagation()}>
                                            <ComposerSlot
                                                id={`composer-link-${index}-attribute`}
                                                value={link.attributeID || undefined}
                                                options={headOptions}
                                                glyph={<AttributeIcon />}
                                                emptyLabel={T._('Choose an attribute', 'Admin')}
                                                searchPlaceholder={T._('Search attributes…', 'Admin')}
                                                missingLabel={T._('Missing attribute', 'Admin')}
                                                isError={hasLinkError(
                                                    index,
                                                    ComposerValidationErrorCode.LINK_INCOMPLETE,
                                                    ComposerValidationErrorCode.LINK_ATTRIBUTE_NOT_FOUND,
                                                    ComposerValidationErrorCode.LINK_SELF_REFERENCE
                                                )}
                                                onChange={(attributeID) => updateLink(index, { ...link, attributeID, optionID: '' })}
                                                onReveal={requestComposerReveal}
                                            />
                                        </HeadSlot>
                                    }
                                    menu={[
                                        [
                                            <DropdownActionItem key='clone' icon={<DuplicateIcon />} onClick={() => cloneLink(index)}>
                                                {T._('Clone', 'Admin')}
                                            </DropdownActionItem>
                                        ],
                                        [
                                            <ConfirmDropdownItem
                                                key='remove'
                                                icon={
                                                    <DropdownItemIcon>
                                                        <DeleteIcon />
                                                    </DropdownItemIcon>
                                                }
                                                onConfirm={() => removeLink(index)}
                                            >
                                                {T._('Remove', 'Admin')}
                                            </ConfirmDropdownItem>
                                        ]
                                    ]}
                                >
                                    <ConditionRow>
                                        <span>{T._('Match', 'Admin')}</span>
                                        <ComposerChoice
                                            ariaLabel={T._('Match', 'Admin')}
                                            options={comparisonOptions}
                                            value={link.operation}
                                            onChange={(operation) => changeOperation(index, link, operation)}
                                        />
                                    </ConditionRow>
                                    <ConditionRow>
                                        <span>{T._('Option', 'Admin')}</span>
                                        {!linkedAttribute ? (
                                            <ApplyNote>{T._('Choose an attribute first.', 'Admin')}</ApplyNote>
                                        ) : multiple ? (
                                            <ComposerSlotList
                                                id={`composer-link-${index}-options`}
                                                value={chosenIDs}
                                                options={optionOptions}
                                                glyph={<OptionIcon />}
                                                caption={linkedAttribute.name}
                                                emptyLabel={T._('Choose an option', 'Admin')}
                                                searchPlaceholder={T._('Search options…', 'Admin')}
                                                missingLabel={T._('Missing option', 'Admin')}
                                                isError={optionError}
                                                onChange={(ids) => updateLink(index, { ...link, optionID: joinComposerLinkOptionIDs(ids) })}
                                                onReveal={requestComposerReveal}
                                            />
                                        ) : (
                                            <ComposerSlot
                                                id={`composer-link-${index}-option`}
                                                value={link.optionID || undefined}
                                                options={optionOptions}
                                                glyph={<OptionIcon />}
                                                caption={linkedAttribute.name}
                                                emptyLabel={T._('Choose an option', 'Admin')}
                                                searchPlaceholder={T._('Search options…', 'Admin')}
                                                missingLabel={T._('Missing option', 'Admin')}
                                                isError={optionError}
                                                onChange={(optionID) => updateLink(index, { ...link, optionID })}
                                                onReveal={requestComposerReveal}
                                            />
                                        )}
                                    </ConditionRow>
                                </ComposerBlock>
                            );
                        })}
                    </ComposerBlockList>
                    <ComposerNewButton type='button' disabled={availableAttributes.length === 0} onClick={addLink}>
                        <PlusIcon />
                        {T._('New condition', 'Admin')}
                    </ComposerNewButton>
                </Rules>
            </ComposerSettingsBody>
        </>
    );
};

// LE CONDIZIONI DI UN PREZZO (prototipo, `priceCard`; utente 2026-10-21): dentro la card del prezzo, senza la barra dei
// Links. Combine in cima, spento finché c'è una condizione sola; ogni condizione è un riquadro più scuro con la parola che
// la lega (If · And if · Or if), l'attributo, Match e Option; «New condition» in fondo. Senza condizioni il prezzo vale
// sempre.
const PriceIf = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px;
    border-radius: 6px;
    background: var(--editor-sunken, #e9ebed);
`;

const ConditionHead = styled(ConditionRow)`
    grid-template-columns: ${COMPOSER_LABEL_COLUMN} minmax(0, 1fr) auto;
`;

export const ComposerPriceConditions: FC<{
    id: string;
    attributes: ComposerAttributeNode[];
    /** Gli attributi che una condizione non può nominare: quelli delle opzioni a cui il prezzo appartiene. */
    excludeAttributeIDs: string[];
    links: ComposerAttributeNodeLink[];
    logicalOperator: ComposerLogicalOperator;
    hasError?: (linkIndex: number) => boolean;
    onChange: (links: ComposerAttributeNodeLink[], logicalOperator: ComposerLogicalOperator) => void;
}> = ({ id, attributes, excludeAttributeIDs, links, logicalOperator, hasError, onChange }) => {
    const availableAttributes = attributes.filter(
        (attribute) => attribute.type === ComposerAttributeType.Attribute && !excludeAttributeIDs.includes(attribute.id) && attribute.options.length > 0
    );
    const attributeOptions: ComposerSlotOption[] = availableAttributes.map((attribute) => ({ value: attribute.id, label: attribute.name }));
    const comparisonOptions = [
        { value: ComposerComparisonOperator.EQUAL, label: T._('is', 'Admin') },
        { value: ComposerComparisonOperator.NOT_EQUAL, label: T._('is not', 'Admin') }
    ];
    const update = (index: number, next: ComposerAttributeNodeLink) =>
        onChange(
            links.map((link, linkIndex) => (linkIndex === index ? next : link)),
            logicalOperator
        );
    const wordOf = (index: number) =>
        index === 0 ? T._('If', 'Admin') : logicalOperator === ComposerLogicalOperator.OR ? T._('Or if', 'Admin') : T._('And if', 'Admin');
    return (
        <>
            <ConditionRow>
                <span>{T._('Combine', 'Admin')}</span>
                <ZTooltip title={links.length < 2 ? T._('Add a second condition to combine them', 'Admin') : undefined}>
                    <div>
                        <ComposerChoice
                            ariaLabel={T._('Combine the conditions', 'Admin')}
                            options={[
                                { value: ComposerLogicalOperator.AND, label: T._('AND', 'Admin') },
                                { value: ComposerLogicalOperator.OR, label: T._('OR', 'Admin') }
                            ]}
                            value={logicalOperator}
                            disabled={links.length < 2}
                            onChange={(operator) => onChange(links, operator)}
                        />
                    </div>
                </ZTooltip>
            </ConditionRow>
            {links.length === 0 && <ComposerFieldNote>{T._('No conditions: this price always applies.', 'Admin')}</ComposerFieldNote>}
            {links.map((link, index) => {
                const linkedAttribute = availableAttributes.find((attribute) => attribute.id === link.attributeID);
                const optionOptions: ComposerSlotOption[] = (linkedAttribute?.options ?? []).map((option) => ({
                    value: option.id,
                    label: option.name,
                    preview: option.img ? <img src={option.img} alt='' /> : undefined
                }));
                const error = hasError?.(index);
                return (
                    <PriceIf key={`${index}_${link.attributeID}`}>
                        <ConditionHead>
                            <span>{wordOf(index)}</span>
                            <ComposerSlot
                                id={`${id}-${index}-attribute`}
                                value={link.attributeID || undefined}
                                options={attributeOptions}
                                glyph={<AttributeIcon />}
                                emptyLabel={T._('Choose an attribute', 'Admin')}
                                searchPlaceholder={T._('Search attributes…', 'Admin')}
                                missingLabel={T._('Missing attribute', 'Admin')}
                                isError={error && !link.attributeID}
                                onChange={(attributeID) => update(index, { ...link, attributeID, optionID: '' })}
                                onReveal={requestComposerReveal}
                            />
                            <ZTooltip title={T._('Remove condition', 'Admin')}>
                                <ComposerRowActionButton
                                    type='button'
                                    aria-label={T._('Remove condition', 'Admin')}
                                    onClick={() =>
                                        onChange(
                                            links.filter((_, linkIndex) => linkIndex !== index),
                                            logicalOperator
                                        )
                                    }
                                >
                                    <DeleteIcon />
                                </ComposerRowActionButton>
                            </ZTooltip>
                        </ConditionHead>
                        <ConditionRow>
                            <span>{T._('Match', 'Admin')}</span>
                            <ComposerChoice
                                ariaLabel={T._('Match', 'Admin')}
                                options={comparisonOptions}
                                value={isComposerMultiOptionLink(link.operation) ? ComposerComparisonOperator.EQUAL : link.operation}
                                onChange={(operation) => update(index, { ...link, operation, optionID: getComposerLinkOptionIDs(link)[0] ?? '' })}
                            />
                        </ConditionRow>
                        <ConditionRow>
                            <span>{T._('Option', 'Admin')}</span>
                            {!linkedAttribute ? (
                                <ApplyNote>{T._('Choose an attribute first.', 'Admin')}</ApplyNote>
                            ) : (
                                <ComposerSlot
                                    id={`${id}-${index}-option`}
                                    value={getComposerLinkOptionIDs(link)[0] || undefined}
                                    options={optionOptions}
                                    glyph={<OptionIcon />}
                                    caption={linkedAttribute.name}
                                    emptyLabel={T._('Choose an option', 'Admin')}
                                    searchPlaceholder={T._('Search options…', 'Admin')}
                                    missingLabel={T._('Missing option', 'Admin')}
                                    isError={error && getComposerLinkOptionIDs(link).length === 0}
                                    onChange={(optionID) => update(index, { ...link, optionID })}
                                    onReveal={requestComposerReveal}
                                />
                            )}
                        </ConditionRow>
                    </PriceIf>
                );
            })}
            <ComposerNewButton
                type='button'
                disabled={availableAttributes.length === 0}
                onClick={() => onChange([...links, { attributeID: '', optionID: '', operation: ComposerComparisonOperator.EQUAL }], logicalOperator)}
            >
                <PlusIcon />
                {T._('New condition', 'Admin')}
            </ComposerNewButton>
        </>
    );
};

export default ComposerLinksEditor;
